import SearchGlyphLoading from './components/SearchGlyphLoading';
import { ISearchInput, SearchInputProps } from './components/SearchInputLoading';
import SearchInputLoading from './components/SearchInputLoading';
import { createLazyComponent, LazyImport, LazyModule, LazyModuleType } from 'owa-bundling';
import { isFeatureEnabled } from 'owa-feature-flags';

const lazyModule = new LazyModule(() =>
    import(/* webpackChunkName: "SearchNextLazy", webpackPrefetch: true */ './lazyIndex')
);

// Actions
export { default as clearSearchBox } from './actions/clearSearchBox';
export { default as endSearchConversation } from './actions/endSearchConversation';
export { default as endSearchSession } from './actions/endSearchSession';
export { default as exitSearch } from './actions/exitSearch';
export { default as getSuggestions } from './actions/getSuggestions';
export { default as onFeedbackSubmitted } from './actions/onFeedbackSubmitted';
export {
    default as onGuidedFormulationSuggestionSelected,
} from './actions/onGuidedFormulationSuggestionSelected';
export { default as onPillSuggestionSelected } from './actions/onPillSuggestionSelected';
export { default as onReceivedSuggestionsToRender } from './actions/onReceivedSuggestionsToRender';
export { default as onRemoveGuidedFormulationPill } from './actions/onRemoveGuidedFormulationPill';
export { default as onRemoveSuggestionPill } from './actions/onRemoveSuggestionPill';
export { default as onSearchInputFocused } from './actions/onSearchInputFocused';
export { default as onSearchRefinersToggled } from './actions/onSearchRefinersToggled';
export { default as onSearchSessionEnded } from './actions/onSearchSessionEnded';
export { default as onSearchSessionStarted } from './actions/onSearchSessionStarted';
export { default as onSearchTextChanged } from './actions/onSearchTextChanged';
export { default as onServiceRequest } from './actions/onServiceRequest';
export { default as onServiceResponse } from './actions/onServiceResponse';
export { default as onSuggestionSelected } from './actions/onSuggestionSelected';
export { default as onSuggestionsProcessed } from './actions/onSuggestionsProcessed';
export { default as resetSearchStore } from './actions/resetSearchStore';
export { default as setSpellerData } from './actions/setSpellerData';
export { default as startSearch } from './actions/startSearch';
export { default as startSearchSession } from './actions/startSearchSession';
export { default as updateNextSearchQueryId } from './actions/updateNextSearchQueryId';

// Init
const lazyInitializeSearch = new LazyImport(lazyModule, m => m.initializeSearch);
export function initializeSearch() {
    if (isFeatureEnabled('sea-lazy2')) {
        lazyInitializeSearch.import().then(initializeSearch => initializeSearch());
    }
}

// Components
export const SearchBoxDropdown = createLazyComponent(lazyModule, m => m.SearchBoxDropdown);
export const SearchBoxPillWell = createLazyComponent(lazyModule, m => m.SearchBoxPillWell);
export const SearchButton = createLazyComponent(lazyModule, m => m.SearchButton);
export const SearchGlyph = createLazyComponent(
    lazyModule,
    m => m.SearchGlyph,
    SearchGlyphLoading // placeholder
);
export const SearchInput = createLazyComponent<
    SearchInputProps,
    LazyModuleType<typeof lazyModule>,
    ISearchInput
>(
    lazyModule,
    m => m.SearchInput,
    SearchInputLoading // placeholder
);
export { ISearchInput } from './components/SearchInputLoading';
export const SuggestionsCallout = createLazyComponent(lazyModule, m => m.SuggestionsCallout);

// Constants
export { START_MATCH_DELIMITER_REGEXP, END_MATCH_DELIMITER_REGEXP } from './exportedConstants';

// Selectors
export const lazyGetHighlightTerms = new LazyImport(lazyModule, m => m.getHighlightTerms);
export const lazyGetSearchBoxContent = new LazyImport(lazyModule, m => m.getSearchBoxContent);
export const lazyGetSearchSessionGuid = new LazyImport(lazyModule, m => m.getSearchSessionGuid);
export const lazyGetSuggestionAtIndex = new LazyImport(lazyModule, m => m.getSuggestionAtIndex);
export const lazyHasSuggestionPills = new LazyImport(lazyModule, m => m.hasSuggestionPills);
export const lazyIsSearchBoxEmpty = new LazyImport(lazyModule, m => m.isSearchBoxEmpty);
export const lazyIsSearchSessionActive = new LazyImport(lazyModule, m => m.isSearchSessionActive);
export const lazyQueryIncludesKQLSuggestion = new LazyImport(
    lazyModule,
    m => m.queryIncludesKQLSuggestion
);

// Store
export const lazyGetSearchStore = new LazyImport(lazyModule, m => m.getSearchStore);
export { LazySearchScenarioStore } from './store/schema/LazySearchStore';

// Utils
export const lazyGetXClientFlightsHeaderValue = new LazyImport(
    lazyModule,
    m => m.getXClientFlightsHeaderValue
);
